"use client";

import { useDeferredValue, useMemo, useState } from "react";
import {
  HighlightedText,
  type TextMatch,
} from "@/components/tool-kit/highlighted-text";
import {
  Notice,
  StatCard,
  StatGrid,
  TextAreaField,
} from "@/components/tool-kit/primitives";
import {
  BUTTON_SECONDARY,
  HINT,
  TABLE,
  TD,
  TH,
  WIDGET_H2,
  WIDGET_H3,
} from "@/components/tool-kit/styles";
import { useToolNumber, useToolUi } from "@/components/tools/tool-ui";
import { t } from "@/lib/i18n/interpolate";
import {
  analyzeAcademicVocabulary,
  NAWL_LEMMA_COUNT,
} from "@/lib/tool-kit/writing-analysis/academic-vocabulary";

/** English strings. A translated content file overrides these through `ui`. */
export const ACADEMIC_VOCABULARY_CHECKER_STRINGS = {
  textLabel: "Your text",
  placeholder: "Paste a paragraph, an essay or a whole paper…",
  privacy: "Runs entirely in your browser — your text is never uploaded.",
  tryExample: "Try an example",
  clear: "Clear",
  exampleText:
    "This longitudinal study examined whether cognitive load mediates the correlation between screen time and sleep quality in adolescents. Using a mixed-methods paradigm, we combined a validated questionnaire with semi-structured interviews and coded the transcripts against an established protocol. The quantitative data showed a modest but statistically reliable association, while the qualitative findings revealed considerable variability in how participants interpreted the items. These results suggest that the relationship is neither linear nor uniform, and that future interventions should be tailored to each student.",
  enterPrompt:
    "Paste or type some text to see which of its words are on the New Academic Word List.",
  results: "Results",
  academicWords: "Academic words",
  coverage: "Coverage",
  lemmas: "Different academic words",
  runningWords: "Running words",
  coverageNote:
    "Coverage is the share of your running words that are on the list. The list leaves out academic words that are also everyday English — research, data, analysis, theory — so those are not highlighted.",
  highlightedHeading: "Your text, highlighted",
  highlightLabel: "Your text with academic words highlighted",
  titleLemma: "On the NAWL as “{lemma}”",
  hoverHint: "Hover over a highlight to see the list entry it counts under.",
  noneFound:
    "None of these words are on the New Academic Word List. Check that the text is in English.",
  tableHeading: "Academic words you used",
  colWord: "List entry",
  colTimes: "Times",
  colForms: "Forms in your text",
  source:
    "Academic words come from the New Academic Word List 1.2 (Browne, Culligan & Phillips, 2013; CC BY-SA 4.0), adapted to {count} entries — details below.",
} as const;

export default function AcademicVocabularyCheckerWidget() {
  const strings = useToolUi(ACADEMIC_VOCABULARY_CHECKER_STRINGS);
  const fmt = useToolNumber();
  const [text, setText] = useState("");
  const deferred = useDeferredValue(text);
  const report = useMemo(() => analyzeAcademicVocabulary(deferred), [deferred]);

  const highlights: TextMatch[] = report.matches.map((match) => ({
    start: match.start,
    end: match.end,
    category: "academic",
    title: t(strings.titleLemma, { lemma: match.lemma }),
  }));
  const hasText = deferred.trim().length > 0;

  return (
    <div className="space-y-6">
      <TextAreaField
        hint={strings.privacy}
        id="av-text"
        label={strings.textLabel}
        onChange={setText}
        placeholder={strings.placeholder}
        prose
        rows={10}
        value={text}
      />
      <div className="flex flex-wrap gap-3">
        <button
          className={BUTTON_SECONDARY}
          onClick={() => setText(strings.exampleText)}
          type="button"
        >
          {strings.tryExample}
        </button>
        <button
          className={BUTTON_SECONDARY}
          disabled={text.length === 0}
          onClick={() => setText("")}
          type="button"
        >
          {strings.clear}
        </button>
      </div>

      {hasText ? (
        <section className="space-y-6">
          <h2 className={WIDGET_H2}>{strings.results}</h2>
          <div aria-live="polite">
            <StatGrid>
              <StatCard
                emphasis
                label={strings.coverage}
                value={fmt(report.coverage, {
                  style: "percent",
                  maximumFractionDigits: 1,
                })}
              />
              <StatCard
                label={strings.academicWords}
                value={fmt(report.academicTokens)}
              />
              <StatCard
                label={strings.lemmas}
                value={fmt(report.lemmas.length)}
              />
              <StatCard
                label={strings.runningWords}
                value={fmt(report.tokens)}
              />
            </StatGrid>
          </div>
          <p className={HINT}>{strings.coverageNote}</p>

          <div className="space-y-3">
            <h3 className={WIDGET_H3}>{strings.highlightedHeading}</h3>
            <HighlightedText
              label={strings.highlightLabel}
              matches={highlights}
              text={deferred}
              tones={{ academic: "emerald" }}
            />
            <p className={HINT}>{strings.hoverHint}</p>
          </div>

          {report.lemmas.length === 0 ? (
            <Notice>{strings.noneFound}</Notice>
          ) : (
            <div className="space-y-3">
              <h3 className={WIDGET_H3}>{strings.tableHeading}</h3>
              <div className="overflow-x-auto rounded-lg border border-border bg-card">
                <table className={TABLE}>
                  <thead>
                    <tr>
                      <th className={TH} scope="col">
                        {strings.colWord}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colTimes}
                      </th>
                      <th className={TH} scope="col">
                        {strings.colForms}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.lemmas.map((row) => (
                      <tr key={row.lemma}>
                        <td className={`${TD} font-medium`}>{row.lemma}</td>
                        <td className={TD}>{fmt(row.count)}</td>
                        <td className={`${TD} text-muted-foreground`}>
                          {row.forms.join(", ")}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </section>
      ) : (
        <Notice>{strings.enterPrompt}</Notice>
      )}

      <p className={HINT}>
        {t(strings.source, { count: fmt(NAWL_LEMMA_COUNT) })}
      </p>
    </div>
  );
}
